import React, { Component } from 'react';
import { computed, observable, toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import autoBindMethods from 'class-autobind-decorator';

import { Button, Grid } from 'react-bootstrap';

import ClientsClass from '../../../clients/ClientsClass';
import { AppConstants } from '../../../constants';
import { ILoanAccount, ILoanCollateralCase } from '../../../interfaces';
import { FormModal } from '../../../lib/mighty-fields';
import { SessionStoreClass } from '../../../stores';
import SmartBool from '../../../utils/SmartBool';
import { Icon, Loader } from '../../common';

import CaseDetailsModal from './CaseDetailsModal';
import CasesTable from './CasesTable';
import { CASE_FIELD_SETS } from './FieldSets';

const { PERMISSIONS } = AppConstants;

interface IProps {
  loanAccount: ILoanAccount;
}

interface IInjected extends IProps {
  Clients: ClientsClass;
  SessionStore: SessionStoreClass;
}

/**
 * The cases the firm is running that the account's loans are secured on. Only law firm accounts have
 * them, so LoanAccount hides this tab on the others.
 */
@inject('Clients', 'SessionStore')
@autoBindMethods
@observer
class TabPrimaryCases extends Component<IProps> {
  @observable private cases: ILoanCollateralCase[] = [];
  @observable private isLoading = true;
  @observable private showAddModal = new SmartBool();
  // The case whose details are showing, or null when the modal is closed
  @observable private viewingCase: null | ILoanCollateralCase = null;

  public componentDidMount () {
    this.fetchCases();
  }

  private get injected () {
    return this.props as IInjected;
  }

  // Not @action: a mobx 3 action does not extend across an await, so the assignments below it would
  // run outside the action anyway.
  private async fetchCases ({ isRefresh = false } = {}) {
    const { loanAccount } = this.props
      , { Clients } = this.injected
      ;

    // A refresh leaves the table up while it runs, the modal that asked for it being open over the
    // top. c.f. TabContacts, which holds its rows up through a re-read for the same reason.
    this.isLoading = !isRefresh;

    try {
      // Every case on the account rather than a page of them, as the loans table reads its loans
      this.cases = await Clients.loanCollateralCases.listAll({ params: { account: loanAccount.id } });
    }
    catch (error) {
      // Transport has already toasted the failure; swallowing it here avoids a second toast.
      // tslint:disable-next-line no-console
      console.error('Error fetching the collateral cases:', error);
    }
    finally {
      this.isLoading = false;
    }
  }

  /**
   * react-table wants a plain array, not the observable one mobx made of the response. Cloned here
   * rather than at the point of use so the array keeps its identity between renders -- see the same
   * note on TabContacts.
   */
  @computed
  private get caseData () {
    return toJS(this.cases);
  }

  private onViewCase (collateralCase: ILoanCollateralCase) {
    this.viewingCase = collateralCase;
  }

  // A new case names the account it belongs to, that being the one this tab is reached through
  private async onCreateCase (data: object) {
    const { loanAccount } = this.props
      , { Clients } = this.injected
      ;

    await Clients.loanCollateralCases.create({ ...data, account: loanAccount.id });
    await this.fetchCases({ isRefresh: true });
  }

  private closeDetailsModal () {
    this.viewingCase = null;
  }

  // Whatever the modal wrote -- an edit to the case, or an update against it -- lands on a row here
  private async onCaseChanged () {
    await this.fetchCases({ isRefresh: true });
  }

  private renderAddButton () {
    const { SessionStore } = this.injected;

    // Creating is rejected without the add permission, so only offer it to users who have it
    if (!SessionStore.userHasPermission(PERMISSIONS.ADD_LOAN_COLLATERAL_CASE)) { return null; }

    return (
      // Not .btn-add: inside a well that class is for link-style buttons and drops the padding
      <Button bsStyle='primary' className='btn-add-case' onClick={this.showAddModal.setTrue}>
        <Icon type='plus-square-o' /> Add new case
      </Button>
    );
  }

  private renderContent () {
    if (this.isLoading) {
      return <Loader className='page-loader' logo />;
    }

    return (
      <CasesTable
        cases={this.caseData}
        newButton={this.renderAddButton()}
        onView={this.onViewCase}
      />
    );
  }

  public render () {
    return (
      <Grid fluid className='loan-account-detail-tab tab-primary-cases'>
        {this.renderContent()}

        {/* The same fields the details modal edits: a firm's existing cases are entered here as they
            stand, which for a case already over means with its resolution */}
        {this.showAddModal.isTrue && (
          <FormModal
            className='modal-collateral-case-form'
            fieldSets={CASE_FIELD_SETS}
            onClose={this.showAddModal.setFalse}
            onSave={this.onCreateCase}
            saveText='Add Case'
            title='Add New Case'
          />
        )}

        {this.viewingCase && (
          <CaseDetailsModal
            collateralCase={this.viewingCase}
            onClose={this.closeDetailsModal}
            onSave={this.onCaseChanged}
          />
        )}
      </Grid>
    );
  }
}

export default TabPrimaryCases;
